/**
 * SessionTimer - Inline Session Countdown for Footer
 *
 * Shows session time remaining:
 * - Black text normally
 * - Red text when < 2 minutes remaining
 * - Triggers countdown modal 30s before expiration
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Clock } from 'lucide-react';
import { useSessionEvents } from '@/hooks/useTokenManager';
import { logout as authLogout } from '@/lib/auth';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

// Configuration
const IDLE_TIMEOUT_SECONDS = 2 * 60 * 60; // 2 hours
const ABSOLUTE_TIMEOUT_SECONDS = 12 * 60 * 60; // 12 hours
const WARNING_THRESHOLD_SECONDS = 2 * 60; // 2 minutes - turn red
const COUNTDOWN_WARNING_SECONDS = 30; // Show modal 30s before expiry
const ACTIVITY_DEBOUNCE_MS = 1000;

function formatTime(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0:00';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function SessionTimer() {
  // Use refs to track timestamps (persist across renders)
  const mountTimeRef = useRef(Date.now());
  const lastActivityRef = useRef(Date.now());

  // State for display
  const [idleSeconds, setIdleSeconds] = useState(IDLE_TIMEOUT_SECONDS);
  const [absoluteSeconds, setAbsoluteSeconds] = useState(ABSOLUTE_TIMEOUT_SECONDS);
  const [showCountdownModal, setShowCountdownModal] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(COUNTDOWN_WARNING_SECONDS);
  const [countdownType, setCountdownType] = useState<'idle' | 'absolute'>('idle');

  // Activity tracking - reset idle timer on user interaction
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleActivity = () => {
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        lastActivityRef.current = Date.now();
        debounceTimer = null;
      }, ACTIVITY_DEBOUNCE_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);

  // Update timers every second
  useEffect(() => {
    const updateTimes = () => {
      const now = Date.now();

      // Idle: time since last activity
      const idleElapsed = Math.floor((now - lastActivityRef.current) / 1000);
      const newIdleSeconds = Math.max(IDLE_TIMEOUT_SECONDS - idleElapsed, 0);
      setIdleSeconds(newIdleSeconds);

      // Absolute: time since component mounted (session start)
      const sessionElapsed = Math.floor((now - mountTimeRef.current) / 1000);
      const newAbsoluteSeconds = Math.max(ABSOLUTE_TIMEOUT_SECONDS - sessionElapsed, 0);
      setAbsoluteSeconds(newAbsoluteSeconds);

      // Check for countdown modal (30s before expiry)
      const minRemaining = Math.min(newIdleSeconds, newAbsoluteSeconds);
      if (minRemaining <= COUNTDOWN_WARNING_SECONDS && minRemaining > 0 && !showCountdownModal) {
        setCountdownType(newIdleSeconds <= newAbsoluteSeconds ? 'idle' : 'absolute');
        setCountdownSeconds(minRemaining);
        setShowCountdownModal(true);
      }
    };

    updateTimes();
    const interval = setInterval(updateTimes, 1000);
    return () => clearInterval(interval);
  }, [showCountdownModal]);

  // Countdown modal timer
  useEffect(() => {
    if (!showCountdownModal) return;

    const interval = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showCountdownModal]);

  // Session events - reset on token refresh
  useSessionEvents({
    onRefreshed: useCallback(() => {
      lastActivityRef.current = Date.now();
      setShowCountdownModal(false);
      setCountdownSeconds(COUNTDOWN_WARNING_SECONDS);
    }, []),
  });

  const handleExtendSession = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        handleLogout();
        return;
      }

      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        if (data.accessToken) {
          localStorage.setItem('accessToken', data.accessToken);
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }
          // Reset timers
          lastActivityRef.current = Date.now();
          setShowCountdownModal(false);
          setCountdownSeconds(COUNTDOWN_WARNING_SECONDS);
          return;
        }
      }
      handleLogout();
    } catch {
      handleLogout();
    }
  };

  const handleLogout = () => {
    authLogout();
    window.location.href = '/';
  };

  // Show the smaller of the two timers
  const displaySeconds = Math.min(idleSeconds, absoluteSeconds);
  const isWarning = displaySeconds <= WARNING_THRESHOLD_SECONDS;

  return (
    <>
      {/* Inline Timer for Footer */}
      <span
        className={cn(
          'flex items-center gap-1 font-mono text-xs transition-colors',
          isWarning ? 'text-red-500 font-medium' : 'text-foreground'
        )}
        title={`Inactividad: ${formatTime(idleSeconds)} | Sesion: ${formatTime(absoluteSeconds)}`}
      >
        <Clock className={cn('h-3 w-3', isWarning && 'animate-pulse')} />
        {formatTime(displaySeconds)}
      </span>

      {/* Countdown Warning Modal */}
      <Dialog open={showCountdownModal} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Clock className="h-5 w-5" />
              Sesion a punto de expirar
            </DialogTitle>
            <DialogDescription>
              {countdownType === 'idle'
                ? 'Tu sesion expirara por inactividad.'
                : 'Has alcanzado el tiempo maximo de sesion.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-6">
            <div className="text-center mb-4">
              <div className="text-5xl font-mono font-bold text-red-600 mb-2">
                {countdownSeconds}
              </div>
              <p className="text-sm text-muted-foreground">segundos restantes</p>
            </div>

            <Progress
              value={(countdownSeconds / COUNTDOWN_WARNING_SECONDS) * 100}
              className="h-2"
            />

            <p className="text-sm text-center text-muted-foreground mt-4">
              Seras redirigido a la pagina de inicio si no te reautentificas.
            </p>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleLogout} className="w-full sm:w-auto">
              Cerrar sesion
            </Button>
            {countdownType === 'idle' && (
              <Button onClick={handleExtendSession} className="w-full sm:w-auto">
                Continuar sesion
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SessionTimer;
